// src/seo/PageSeo.jsx
//
// Componente único que injeta <title>, <meta description>, <link canonical>,
// Open Graph, Twitter Card e (opcionalmente) JSON-LD por rota pública.
//
// O HTML base em `public/index.html` já carrega o JSON-LD canônico (Organization /
// MedicalBusiness / WebSite / Service / FAQPage). Este componente complementa com
// metas dinâmicas por rota e BreadcrumbList — o Helmet substitui as tags do head
// no client-side, e bots como Googlebot que executam JS leem o resultado final.
// Para LLMs que não executam JS, o conteúdo canônico já está no HTML base.

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { publicOriginForSeo } from '../lib/siteHosts';

const SITE_NAME = 'Consulta+';

/**
 * @param {object} props
 * @param {string} props.title       Title da aba (recomendado <= 60 chars).
 * @param {string} props.description Meta description (recomendado 140-160 chars).
 * @param {string} props.path        Caminho relativo (ex.: "/diretor"). Vira canonical e og:url.
 * @param {string} [props.image]     URL absoluta de imagem OG. Padrão: logo.png.
 * @param {boolean} [props.noindex]  Se true, emite robots noindex,nofollow.
 * @param {Array<{name: string, url?: string}>} [props.breadcrumbs]
 *        Trilha para BreadcrumbList JSON-LD. Primeiro item normalmente é
 *        { name: 'Início', url: '/' }.
 * @param {object} [props.jsonLd]    JSON-LD adicional específico da rota
 *        (objeto único ou array). Será serializado em <script type="application/ld+json">.
 */
export default function PageSeo({
  title,
  description,
  path = '/',
  image,
  noindex = false,
  breadcrumbs,
  jsonLd,
}) {
  const siteUrl = publicOriginForSeo();
  const url = `${siteUrl}${path === '/' ? '/' : path.replace(/\/+$/, '')}`;
  const ogImage = image || `${siteUrl}/logo.png`;
  // Reconhece variantes do brand para não duplicar sufixo (ex.: já tem
  // "Consulta+", "Consulta Mais" ou "ConsultaMais" no título).
  const hasBrand = /Consulta\+?|Consulta\s*Mais/i.test(title || '');
  const fullTitle = hasBrand ? title : `${title} | ${SITE_NAME}`;

  const breadcrumbJson =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: b.name,
            ...(b.url ? { item: `${siteUrl}${b.url}` } : {}),
          })),
        }
      : null;

  const extraJsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <html lang="pt-BR" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {breadcrumbJson && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJson)}
        </script>
      )}
      {extraJsonLdArray.map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
